import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Project } from '@/types/project';
import { CheckCircle, XCircle, Copy, FileText } from 'lucide-react';

interface ProjectImportDialogProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ImportFormData {
  // Google Sheets
  proc_file: string;
  doc_file: string;
  shop_file: string;
  cost_file: string;
  // Google Drive folders
  project_folder: string;
  build_folder: string;
  doc_folder: string;
  req_folder: string;
  proposal_folder: string;
  tech_folder: string;
  report_folder: string;
  specs_folder: string;
  po_folder: string;
  shop_folder: string;
}

const validateGoogleSheetsUrl = (url: string): boolean => {
  if (!url.trim()) return true; // Empty URLs are allowed
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'docs.google.com' && url.includes('/spreadsheets/');
  } catch {
    return false;
  }
};

const validateGoogleDriveUrl = (url: string): boolean => {
  if (!url.trim()) return true; // Empty URLs are allowed
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'drive.google.com' && url.includes('/folders/');
  } catch {
    return false;
  }
};

export function ProjectImportDialog({ project, isOpen, onClose }: ProjectImportDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [importMode, setImportMode] = useState<'individual' | 'bulk'>('individual');
  const [bulkInput, setBulkInput] = useState('');
  const [parsedData, setParsedData] = useState<ImportFormData | null>(null);
  
  const [formData, setFormData] = useState<ImportFormData>({
    // Google Sheets
    proc_file: project?.proc_file || '',
    doc_file: project?.doc_file || '',
    shop_file: project?.shop_file || '',
    cost_file: project?.cost_file || '',
    // Google Drive folders
    project_folder: project?.project_folder || '',
    build_folder: project?.build_folder || '',
    doc_folder: project?.doc_folder || '',
    req_folder: project?.req_folder || '',
    proposal_folder: project?.proposal_folder || '',
    tech_folder: project?.tech_folder || '',
    report_folder: project?.report_folder || '',
    specs_folder: project?.specs_folder || '',
    po_folder: project?.po_folder || '',
    shop_folder: project?.shop_folder || '',
  });

  // Reset form when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        // Google Sheets
        proc_file: project.proc_file || '',
        doc_file: project.doc_file || '',
        shop_file: project.shop_file || '',
        cost_file: project.cost_file || '',
        // Google Drive folders
        project_folder: project.project_folder || '',
        build_folder: project.build_folder || '',
        doc_folder: project.doc_folder || '',
        req_folder: project.req_folder || '',
        proposal_folder: project.proposal_folder || '',
        tech_folder: project.tech_folder || '',
        report_folder: project.report_folder || '',
        specs_folder: project.specs_folder || '',
        po_folder: project.po_folder || '',
        shop_folder: project.shop_folder || '',
      });
    }
  }, [project]);

  const updateImportMutation = useMutation({
    mutationFn: async (data: ImportFormData) => {
      if (!project) throw new Error('No project selected');

      // Validate Google Sheets URLs
      const gsheetsToValidate = [
        { url: data.proc_file, field: 'Procurement' },
        { url: data.doc_file, field: 'Documentation' },
        { url: data.shop_file, field: 'Shop Drawing' },
        { url: data.cost_file, field: 'Cost' },
      ];

      for (const { url, field } of gsheetsToValidate) {
        if (!validateGoogleSheetsUrl(url)) {
          throw new Error(`Invalid Google Sheets URL for ${field}. Please use a valid Google Sheets link.`);
        }
      }

      // Validate Google Drive URLs
      const gdriveToValidate = [
        { url: data.project_folder, field: 'Project Folder' },
        { url: data.build_folder, field: 'Build Folder' },
        { url: data.doc_folder, field: 'Doc Folder' },
        { url: data.req_folder, field: 'Requirements Folder' },
        { url: data.proposal_folder, field: 'Proposal Folder' },
        { url: data.tech_folder, field: 'Tech Folder' },
        { url: data.report_folder, field: 'Report Folder' },
        { url: data.specs_folder, field: 'Specs Folder' },
        { url: data.po_folder, field: 'PO Folder' },
        { url: data.shop_folder, field: 'Shop Folder' },
      ];

      for (const { url, field } of gdriveToValidate) {
        if (!validateGoogleDriveUrl(url)) {
          throw new Error(`Invalid Google Drive URL for ${field}. Please use a valid Google Drive folder link.`);
        }
      }

      const { error } = await supabase
        .from('projects')
        .update({
          // Google Sheets
          proc_file: data.proc_file || null,
          doc_file: data.doc_file || null,
          shop_file: data.shop_file || null,
          cost_file: data.cost_file || null,
          // Google Drive folders
          project_folder: data.project_folder || null,
          build_folder: data.build_folder || null,
          doc_folder: data.doc_folder || null,
          req_folder: data.req_folder || null,
          proposal_folder: data.proposal_folder || null,
          tech_folder: data.tech_folder || null,
          report_folder: data.report_folder || null,
          specs_folder: data.specs_folder || null,
          po_folder: data.po_folder || null,
          shop_folder: data.shop_folder || null,
        })
        .eq('id', project.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Import links updated successfully",
      });
      // Invalidate all project-related queries
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
      queryClient.invalidateQueries({ predicate: (query) => 
        query.queryKey.some(key => typeof key === 'string' && key.includes('project'))
      });
      // Force a complete cache refresh
      queryClient.refetchQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateImportMutation.mutate(formData);
  };

  const handleInputChange = (field: keyof ImportFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Field order for parsing (fixed positions)
  const fieldOrder: (keyof ImportFormData)[] = [
    // Google Sheets (0-3)
    'proc_file', 'doc_file', 'shop_file', 'cost_file',
    // Google Drive Folders (4-13)
    'project_folder', 'build_folder', 'doc_folder', 'req_folder', 
    'proposal_folder', 'tech_folder', 'report_folder', 'specs_folder', 
    'po_folder', 'shop_folder'
  ];

  const fieldLabels: Record<keyof ImportFormData, string> = {
    proc_file: 'Procurement',
    doc_file: 'Documentation', 
    shop_file: 'Shop Drawing',
    cost_file: 'Cost',
    project_folder: 'Project Folder',
    build_folder: 'Build Folder',
    doc_folder: 'Doc Folder',
    req_folder: 'Requirements Folder',
    proposal_folder: 'Proposal Folder',
    tech_folder: 'Tech Folder',
    report_folder: 'Report Folder',
    specs_folder: 'Specs Folder',
    po_folder: 'PO Folder',
    shop_folder: 'Shop Folder',
  };

  const parseBulkLinks = (input: string): ImportFormData => {
    const links = input.split(',').map(link => link.trim());
    const parsed: ImportFormData = {} as ImportFormData;
    
    fieldOrder.forEach((field, index) => {
      parsed[field] = links[index] || '';
    });
    
    return parsed;
  };

  const validateParsedData = (data: ImportFormData) => {
    const results: Array<{field: string, url: string, isValid: boolean, type: 'sheets' | 'drive'}> = [];
    
    // Validate Google Sheets (first 4)
    fieldOrder.slice(0, 4).forEach(field => {
      const url = data[field];
      results.push({
        field: fieldLabels[field],
        url,
        isValid: validateGoogleSheetsUrl(url),
        type: 'sheets'
      });
    });
    
    // Validate Google Drive (last 10)
    fieldOrder.slice(4).forEach(field => {
      const url = data[field];
      results.push({
        field: fieldLabels[field],
        url,
        isValid: validateGoogleDriveUrl(url),
        type: 'drive'
      });
    });
    
    return results;
  };

  const handleParseBulk = () => {
    if (!bulkInput.trim()) {
      toast({
        title: "Error",
        description: "Please paste links to parse",
        variant: "destructive",
      });
      return;
    }
    
    const parsed = parseBulkLinks(bulkInput);
    setParsedData(parsed);
  };

  const handleApplyToForm = () => {
    if (parsedData) {
      setFormData(parsedData);
      setImportMode('individual');
      toast({
        title: "Success",
        description: "Links applied to form successfully",
      });
    }
  };

  const handleExportCurrent = () => {
    const linkString = fieldOrder.map(field => formData[field]).join(', ');
    navigator.clipboard.writeText(linkString);
    toast({
      title: "Success", 
      description: "Current links copied to clipboard",
    });
  };

  const handleClearAll = () => {
    const emptyData: ImportFormData = {} as ImportFormData;
    fieldOrder.forEach(field => {
      emptyData[field] = '';
    });
    setFormData(emptyData);
  };

  const handleFillTemplate = () => {
    setBulkInput('https://docs.google.com/spreadsheets/d/example1..., https://docs.google.com/spreadsheets/d/example2..., , , https://drive.google.com/drive/folders/example1..., https://drive.google.com/drive/folders/example2..., , , , , , , , ');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Links</DialogTitle>
        </DialogHeader>
        
        <Tabs value={importMode} onValueChange={(value) => setImportMode(value as 'individual' | 'bulk')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="individual">Individual Input</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
          </TabsList>
          
          <TabsContent value="bulk" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Bulk Import Links
                </CardTitle>
                 <div className="text-sm text-muted-foreground space-y-2">
                   <p>📝 Paste 14 links separated by commas in this order:</p>
                   <div className="bg-muted p-3 rounded-md text-xs">
                     <p><strong>Google Sheets (4):</strong> Procurement, Documentation, Shop Drawing, Cost</p>
                     <p><strong>Google Drive Folders (10):</strong> Project, Build, Doc, Requirements, Proposal, Tech, Report, Specs, PO, Shop</p>
                     <p className="text-orange-600 mt-2"><strong>Note:</strong> Use empty spaces (,,) for missing links</p>
                   </div>
                   <p className="text-sm text-blue-600">💡 Workflow: Paste → Parse Links → Apply to Form → Switch to Individual tab</p>
                 </div>
              </CardHeader>
              <CardContent className="space-y-4">
                
                <Textarea
                  placeholder="Paste comma-separated links here..."
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  className="min-h-[120px]"
                />
                
                <div className="flex gap-2">
                  <Button type="button" onClick={handleParseBulk}>
                    Parse Links
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setBulkInput('')}>
                    Clear
                  </Button>
                </div>
                
                {parsedData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Preview Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {validateParsedData(parsedData).map((result, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            {result.isValid ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <Badge variant={result.type === 'sheets' ? 'default' : 'secondary'}>
                              {result.field}
                            </Badge>
                            <span className="text-muted-foreground truncate flex-1">
                              {result.url || 'Empty'}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button onClick={handleApplyToForm}>
                          Apply to Form
                        </Button>
                        <Button variant="outline" onClick={() => setParsedData(null)}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="individual">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-end gap-2 mb-4">
                <Button type="button" variant="outline" size="sm" onClick={handleExportCurrent}>
                  <Copy className="h-4 w-4 mr-1" />
                  Export Links
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleClearAll}>
                  Clear All
                </Button>
              </div>
          {/* Google Sheets Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Google Sheets</h3>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="proc_file" className="flex items-center gap-2">
                  <span className="text-lg">🛒</span>
                  Procurement Google Sheet
                </Label>
                <Input
                  id="proc_file"
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={formData.proc_file}
                  onChange={(e) => handleInputChange('proc_file', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="doc_file" className="flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  Documentation Google Sheet
                </Label>
                <Input
                  id="doc_file"
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={formData.doc_file}
                  onChange={(e) => handleInputChange('doc_file', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shop_file" className="flex items-center gap-2">
                  <span className="text-lg">📐</span>
                  Shop Drawing Google Sheet
                </Label>
                <Input
                  id="shop_file"
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={formData.shop_file}
                  onChange={(e) => handleInputChange('shop_file', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost_file" className="flex items-center gap-2">
                  <span className="text-lg">💰</span>
                  Cost Google Sheet
                </Label>
                <Input
                  id="cost_file"
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={formData.cost_file}
                  onChange={(e) => handleInputChange('cost_file', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Google Drive Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Google Drive Folders</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project_folder" className="flex items-center gap-2">
                  <span className="text-lg">📁</span>
                  Project Folder
                </Label>
                <Input
                  id="project_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.project_folder}
                  onChange={(e) => handleInputChange('project_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="build_folder" className="flex items-center gap-2">
                  <span className="text-lg">🏗️</span>
                  Build Folder
                </Label>
                <Input
                  id="build_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.build_folder}
                  onChange={(e) => handleInputChange('build_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="doc_folder" className="flex items-center gap-2">
                  <span className="text-lg">📄</span>
                  Doc Folder
                </Label>
                <Input
                  id="doc_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.doc_folder}
                  onChange={(e) => handleInputChange('doc_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="req_folder" className="flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  Requirements Folder
                </Label>
                <Input
                  id="req_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.req_folder}
                  onChange={(e) => handleInputChange('req_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="proposal_folder" className="flex items-center gap-2">
                  <span className="text-lg">💼</span>
                  Proposal Folder
                </Label>
                <Input
                  id="proposal_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.proposal_folder}
                  onChange={(e) => handleInputChange('proposal_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tech_folder" className="flex items-center gap-2">
                  <span className="text-lg">⚙️</span>
                  Tech Folder
                </Label>
                <Input
                  id="tech_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.tech_folder}
                  onChange={(e) => handleInputChange('tech_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="report_folder" className="flex items-center gap-2">
                  <span className="text-lg">📊</span>
                  Report Folder
                </Label>
                <Input
                  id="report_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.report_folder}
                  onChange={(e) => handleInputChange('report_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specs_folder" className="flex items-center gap-2">
                  <span className="text-lg">📐</span>
                  Specs Folder
                </Label>
                <Input
                  id="specs_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.specs_folder}
                  onChange={(e) => handleInputChange('specs_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="po_folder" className="flex items-center gap-2">
                  <span className="text-lg">🧾</span>
                  PO Folder
                </Label>
                <Input
                  id="po_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.po_folder}
                  onChange={(e) => handleInputChange('po_folder', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shop_folder" className="flex items-center gap-2">
                  <span className="text-lg">🏪</span>
                  Shop Folder
                </Label>
                <Input
                  id="shop_folder"
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={formData.shop_folder}
                  onChange={(e) => handleInputChange('shop_folder', e.target.value)}
                />
              </div>
            </div>
          </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateImportMutation.isPending}
                >
                  {updateImportMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}